// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface AffiliateStats {
  total_earnings: number;
  total_clicks: number;
  total_conversions: number;
  pending_payouts: number;
}

export const useAffiliateStats = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState<AffiliateStats>({
    total_earnings: 0,
    total_clicks: 0,
    total_conversions: 0,
    pending_payouts: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAffiliateStats = async () => {
      if (!profile?.id) {
        console.warn('No profile ID found for affiliate stats');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching affiliate record for profile_id:', profile.id);

        // First, fetch affiliate record using profile.id as user_id
        const { data: affiliateData, error: affiliateError } = await supabase
          .from('affiliates')
          .select('id, user_id, status, total_earnings')
          .eq('user_id', profile.id)
          .maybeSingle();

        if (affiliateError) {
          console.error('Error fetching affiliate record:', affiliateError);
          throw affiliateError;
        }

        if (!affiliateData) {
          console.warn(`No affiliate record found for profile_id: ${profile.id}`);
          setStats({
            total_earnings: 0,
            total_clicks: 0,
            total_conversions: 0,
            pending_payouts: 0,
          });
          setLoading(false);
          return;
        }

        console.log('Found affiliate record:', affiliateData);
        const affiliateId = affiliateData.id;
        console.log('Using affiliateId for stats:', affiliateId);

        // Fetch aggregate stats from affiliate_stats using affiliateId
        console.log('Query affiliate_stats with affiliate_id:', affiliateId);
        const { data: statsRows, error: statsError } = await supabase
          .from('affiliate_stats')
          .select('total_earnings,total_clicks,total_conversions,pending_payouts')
          .eq('affiliate_id', affiliateId);

        if (statsError) {
          console.error('Error fetching affiliate_stats:', statsError);
          throw statsError;
        }

        // Sum across all dates
        const sumNumber = (arr: any[] | null | undefined, key: string) =>
          (arr || []).reduce((sum, row) => {
            const val = (row as any)[key];
            const num = typeof val === 'string' ? parseFloat(val) : (typeof val === 'number' ? val : 0);
            return sum + (isNaN(num) ? 0 : num);
          }, 0);

        // Try server-side totals via RPC to avoid RLS mismatches
        const { data: totals, error: totalsError } = await supabase.rpc('get_affiliate_overview_totals');
        if (totalsError) {
          console.warn('RPC get_affiliate_overview_totals failed, falling back to client aggregation:', totalsError);
        }

        let calculatedStats: AffiliateStats;
        if (totals && (totals as any[]).length > 0) {
          const t: any = (totals as any[])[0];
          calculatedStats = {
            total_earnings: typeof t.total_earnings === 'string' ? parseFloat(t.total_earnings) : (t.total_earnings || 0),
            total_clicks: t.total_clicks || 0,
            total_conversions: t.total_conversions || 0,
            pending_payouts: typeof t.pending_payouts === 'string' ? parseFloat(t.pending_payouts) : (t.pending_payouts || 0),
          };
          console.log('Using RPC affiliate totals:', { affiliateId, calculatedStats });
        } else {
          // Fallback: Sum across all dates from affiliate_stats
          const totalEarnings = sumNumber(statsRows, 'total_earnings');
          const totalClicks = sumNumber(statsRows, 'total_clicks');
          const totalConversions = sumNumber(statsRows, 'total_conversions');
          const pendingPayouts = sumNumber(statsRows, 'pending_payouts');

          calculatedStats = {
            total_earnings: totalEarnings,
            total_clicks: totalClicks,
            total_conversions: totalConversions,
            pending_payouts: pendingPayouts,
          };
          console.log('Aggregated affiliate_stats (fallback):', { rows: statsRows?.length || 0, calculatedStats });
        }

        setStats(calculatedStats);

      } catch (err) {
        console.error('Error fetching affiliate stats:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliateStats();
  }, [profile?.id]);

  return { stats, loading, error };
};
